import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { authApi, organizationApi, permissionsApi } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import {
    formatPermissionEffectLabel,
    formatPermissionDesignatorLabel,
    formatPermissionActionVerbList,
    formatPermissionResourceTypePluralLabel,
} from '@/components/permissions/PermissionGrantSummary';
import { PermissionGrantEditor } from '@/components/permissions/PermissionGrantEditor';
import {
    type ActiveRoleAssignmentResponse,
    type OrganizationPermissionAuditResponse,
    type PermissionGrantAuditFinding,
    type PermissionsGrant,
    type RoleAssignmentPermissionAuditResponse,
    isAdminRoleAssignment,
} from '@pillar-portal/api-types';
import {
    AuthorizableAction,
    ResourceType,
} from '@pillar-portal/database/dist/frontend';

const OrganizationUuidSchema: z.ZodType<string> = z.string().uuid();
const RoleAssignmentUuidSchema: z.ZodType<string> = z.string().uuid();

interface EditableAuditFindingContext {
    role_assignment_uuid: string;
    user_display_name: string;
    organization_uuid: string | null;
    finding: PermissionGrantAuditFinding;
}

function buildEmptyPermissionGrant(): PermissionsGrant {
    return {
        effect: 'allow',
        resource_type: ResourceType.CREDENTIAL,
        actions: [],
        resources: [],
    };
}

function formatAuditFindingCodeLabel(code: string): string {
    return code
        .toLowerCase()
        .split('_')
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
}

function isEditableAuditFinding(finding: PermissionGrantAuditFinding): boolean {
    return finding.permissions_bundle_uuid !== null && finding.permission_index !== null;
}

export function PermissionAuditPage() {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const queryClient = useQueryClient();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const [editingFinding, setEditingFinding] =
        useState<EditableAuditFindingContext | null>(null);
    const [draftPermission, setDraftPermission] = useState<PermissionsGrant>(
        buildEmptyPermissionGrant()
    );
    const [repairValidationError, setRepairValidationError] = useState<string | null>(null);
    useUnsavedChangesRegistration(editingFinding !== null);

    const organizationUuid = useMemo(() => {
        const organizationUuidCandidate = params.id;
        if (organizationUuidCandidate === undefined) {
            return null;
        }
        const parseResult = OrganizationUuidSchema.safeParse(organizationUuidCandidate);
        return parseResult.success ? parseResult.data : null;
    }, [params.id]);

    const focusedRoleAssignmentUuid = useMemo(() => {
        const roleAssignmentUuidCandidate = searchParams.get('role_assignment_uuid');
        if (roleAssignmentUuidCandidate === null) {
            return null;
        }
        const parseResult = RoleAssignmentUuidSchema.safeParse(roleAssignmentUuidCandidate);
        return parseResult.success ? parseResult.data : null;
    }, [searchParams]);

    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });

    const organizationQuery = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'permissions',
            'audit',
            'organization',
            organizationUuid,
            'detail',
        ]),
        queryFn: async () => {
            if (organizationUuid === null) {
                throw new Error('Invalid organization UUID');
            }
            return await organizationApi.getOrganization(organizationUuid);
        },
        enabled: organizationUuid !== null,
        staleTime: 30000,
    });

    const canAuditPermissions = isAdminRoleAssignment(
        activeRoleAssignmentQuery.data ?? null
    );

    const organizationAuditQuery = useQuery<OrganizationPermissionAuditResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'permissions',
            'audit',
            'organization',
            organizationUuid,
        ]),
        queryFn: async () => {
            if (organizationUuid === null) {
                throw new Error('Invalid organization UUID');
            }
            return await permissionsApi.auditOrganizationPermissions(organizationUuid);
        },
        enabled: organizationUuid !== null && canAuditPermissions,
        staleTime: 30000,
    });

    const focusedRoleAssignmentAuditQuery = useQuery<RoleAssignmentPermissionAuditResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'permissions',
            'audit',
            'roleAssignment',
            focusedRoleAssignmentUuid,
        ]),
        queryFn: async () => {
            if (focusedRoleAssignmentUuid === null) {
                throw new Error('Invalid role assignment UUID');
            }
            return await permissionsApi.auditRoleAssignmentPermissions(
                focusedRoleAssignmentUuid
            );
        },
        enabled: focusedRoleAssignmentUuid !== null && canAuditPermissions,
        staleTime: 30000,
    });

    const repairMutation = useMutation({
        mutationFn: async (request: {
            permissions_bundle_uuid: string;
            permission_index: number;
            permission: PermissionsGrant;
        }) => await permissionsApi.repairAuditedPermission(request),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                    'permissions',
                    'audit',
                ]),
            });
            setEditingFinding(null);
        },
    });
    const repairResetRef = useRef(repairMutation.reset);

    useEffect(() => {
        repairResetRef.current = repairMutation.reset;
    }, [repairMutation.reset]);

    useEffect(() => {
        if (editingFinding === null) {
            setDraftPermission(buildEmptyPermissionGrant());
            setRepairValidationError(null);
            return;
        }
        setDraftPermission(
            editingFinding.finding.editable_permission ?? buildEmptyPermissionGrant()
        );
        setRepairValidationError(null);
        repairResetRef.current();
    }, [editingFinding]);

    const repairModalDescription = useMemo(() => {
        if (editingFinding === null || draftPermission.actions.length === 0) {
            return 'Select actions to grant permissions for.';
        }
        return `${formatPermissionEffectLabel(draftPermission.effect)} ${formatPermissionActionVerbList(draftPermission.actions)} permissions to ${editingFinding.user_display_name} on ${formatPermissionResourceTypePluralLabel(draftPermission.resource_type)}`;
    }, [
        draftPermission.actions,
        draftPermission.effect,
        draftPermission.resource_type,
        editingFinding,
    ]);
    const repairErrorMessage =
        repairValidationError ??
        (repairMutation.error instanceof Error ? repairMutation.error.message : null);

    const handleRepairSave = async (): Promise<void> => {
        if (
            editingFinding === null ||
            editingFinding.finding.permissions_bundle_uuid === null ||
            editingFinding.finding.permission_index === null
        ) {
            setRepairValidationError('Selected finding cannot be repaired.');
            return;
        }
        if (draftPermission.actions.length === 0) {
            setRepairValidationError('Select at least one action');
            return;
        }
        if (draftPermission.resources.length === 0) {
            setRepairValidationError('Select at least one source designator');
            return;
        }
        setRepairValidationError(null);
        await repairMutation.mutateAsync({
            permissions_bundle_uuid: editingFinding.finding.permissions_bundle_uuid,
            permission_index: editingFinding.finding.permission_index,
            permission: draftPermission,
        });
    };

    if (organizationUuid === null) {
        return <div className="text-destructive">Invalid organization UUID.</div>;
    }

    if (
        activeRoleAssignmentQuery.isLoading ||
        organizationQuery.isLoading ||
        (focusedRoleAssignmentUuid !== null && focusedRoleAssignmentAuditQuery.isLoading) ||
        organizationAuditQuery.isLoading
    ) {
        return <div>Loading...</div>;
    }

    if (!canAuditPermissions) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    Insufficient permissions to audit organization grants.
                </AlertDescription>
            </Alert>
        );
    }

    if (organizationQuery.error) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    {organizationQuery.error instanceof Error
                        ? organizationQuery.error.message
                        : 'Failed to load organization.'}
                </AlertDescription>
            </Alert>
        );
    }

    if (organizationAuditQuery.error) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    {organizationAuditQuery.error instanceof Error
                        ? organizationAuditQuery.error.message
                        : 'Failed to audit organization grants.'}
                </AlertDescription>
            </Alert>
        );
    }

    const focusedAudit = focusedRoleAssignmentAuditQuery.data?.audit ?? null;
    const organizationAudit = organizationAuditQuery.data;
    const totalFindings =
        organizationAudit?.audits.reduce(
            (sum, audit) => sum + audit.findings.length,
            0
        ) ?? 0;

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold">Permissions Audit</h1>
                    <p className="text-sm text-muted-foreground">
                        Review persisted permission grants for users in this
                        organization tree.
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() =>
                        breadcrumbNavigate({
                            source: 'panel',
                            target: {
                                url: `/organizations/${organizationUuid}`,
                                title: 'Organization Details',
                            },
                        })
                    }
                >
                    Back to Organization
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>{organizationQuery.data?.name ?? 'Organization'}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center gap-3 text-sm">
                    <Badge variant="secondary">
                        {organizationAudit?.audits.length ?? 0} role assignments
                    </Badge>
                    <Badge variant={totalFindings > 0 ? 'destructive' : 'secondary'}>
                        {totalFindings} findings
                    </Badge>
                    {focusedRoleAssignmentUuid !== null && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url: `/organizations/${organizationUuid}/permissions-audit`,
                                        title: 'Permissions Audit',
                                    },
                                })
                            }
                        >
                            Clear Role Filter
                        </Button>
                    )}
                </CardContent>
            </Card>

            {focusedAudit !== null && (
                <Card>
                    <CardHeader>
                        <CardTitle>Focused Role Assignment Audit</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        <div className="font-medium">{focusedAudit.user_display_name}</div>
                        <div className="text-muted-foreground">
                            {focusedAudit.primary_email}
                        </div>
                        <div className="text-muted-foreground">
                            {focusedAudit.findings.length} findings
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="space-y-4">
                {organizationAudit?.audits.map((audit) => (
                    <Card key={audit.role_assignment_uuid}>
                        <CardHeader className="flex flex-row items-start justify-between gap-4">
                            <div>
                                <CardTitle className="text-base">
                                    {audit.user_display_name}
                                </CardTitle>
                                <div className="text-sm text-muted-foreground">
                                    {audit.primary_email}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge
                                    variant={
                                        audit.findings.length > 0
                                            ? 'destructive'
                                            : 'secondary'
                                    }
                                >
                                    {audit.findings.length} findings
                                </Badge>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        breadcrumbNavigate({
                                            source: 'panel',
                                            target: {
                                                url:
                                                    `/organizations/${organizationUuid}/permissions-audit` +
                                                    `?role_assignment_uuid=${audit.role_assignment_uuid}`,
                                                title: 'Permissions Audit',
                                            },
                                        })
                                    }
                                >
                                    Focus
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {audit.findings.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    No invalid permission grants found.
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {audit.findings.map((finding, index) => (
                                        <div
                                            key={`${audit.role_assignment_uuid}-${index}`}
                                            className="rounded border p-3 text-sm"
                                        >
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Badge variant="outline">
                                                        {formatAuditFindingCodeLabel(
                                                            finding.code
                                                        )}
                                                    </Badge>
                                                    <span className="text-muted-foreground">
                                                        {finding.permissions_bundle_name ??
                                                            'Unknown bundle'}
                                                    </span>
                                                    {finding.permission_index !== null && (
                                                        <span className="text-muted-foreground">
                                                            Permission #
                                                            {finding.permission_index + 1}
                                                        </span>
                                                    )}
                                                    {finding.resource_index !== null && (
                                                        <span className="text-muted-foreground">
                                                            Resource #
                                                            {finding.resource_index + 1}
                                                        </span>
                                                    )}
                                                </div>
                                                {isEditableAuditFinding(finding) && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() =>
                                                            setEditingFinding({
                                                                role_assignment_uuid:
                                                                    audit.role_assignment_uuid,
                                                                user_display_name:
                                                                    audit.user_display_name,
                                                                organization_uuid:
                                                                    audit.organization_uuid,
                                                                finding,
                                                            })
                                                        }
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                            </div>
                                            <p className="mt-2">{finding.message}</p>
                                            {finding.source_designator !== null && (
                                                <p className="mt-2 text-muted-foreground">
                                                    {formatPermissionDesignatorLabel(
                                                        finding.source_designator
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog
                open={editingFinding !== null}
                onOpenChange={(nextOpen) => {
                    if (!nextOpen) {
                        setEditingFinding(null);
                    }
                }}
            >
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit Permission</DialogTitle>
                        <DialogDescription>{repairModalDescription}</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6">
                        <PermissionGrantEditor
                            value={draftPermission}
                            onChange={(nextPermission) => {
                                setDraftPermission(nextPermission);
                                setRepairValidationError(null);
                            }}
                            eligibleActions={Object.values(AuthorizableAction)}
                            organizationUuid={
                                editingFinding?.organization_uuid ?? organizationUuid
                            }
                        />

                        {repairErrorMessage !== null && (
                            <Alert variant="destructive">
                                <AlertDescription>{repairErrorMessage}</AlertDescription>
                            </Alert>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setEditingFinding(null)}
                            disabled={repairMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={() => void handleRepairSave()}
                            disabled={repairMutation.isPending}
                        >
                            {repairMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
